import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  X,
  ArrowLeft,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { devWarn, devError } from "@/utils/devLog";

// Point PDF.js at a public CDN worker that matches the bundled version.
// unpkg.com mirrors the exact pdfjs-dist version we have installed, which
// guarantees the worker and the API code stay in lockstep (cdnjs sometimes
// lags major releases by a few weeks).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimum horizontal swipe distance (in px) before we treat it as a page turn.
const SWIPE_THRESHOLD = 50;

/**
 * PdfViewer — fullscreen black overlay that renders a PDF document one page
 * at a time using Mozilla's PDF.js (via react-pdf), giving identical results
 * on every platform — including iOS Safari and Android Chrome.
 *
 * Interactions:
 *   • ← / → keyboard arrows or chevron buttons: previous / next page.
 *   • Touch swipe left / right: previous / next page (mobile app feel).
 *   • Page counter (e.g. "3 / 8") sits in the caption.
 *   • Top-left back button returns to the repository index.
 *   • Top-right download button opens an email-gated download modal that
 *     dispatches a Resend notification to the studio before the file
 *     download is triggered in the visitor's browser.
 */
const PdfViewer = ({ document: doc }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(900);
  const [pageAspect, setPageAspect] = useState(null); // first-page width/height ratio
  const [loadError, setLoadError] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const wrapRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const valid = EMAIL_RX.test(email.trim());

  // Lock body scroll while the viewer is mounted
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Compute the right page width for the current viewport.
  //   • Portrait phones: fit to width — keep the current mobile experience.
  //   • Landscape / desktop: fit so one page fills the available height,
  //     so the visitor never has to scroll inside a page; flipping arrows
  //     advance the magazine. We need the page's natural aspect ratio,
  //     which we get from <Page onLoadSuccess>; until it lands we use a
  //     sensible fallback (US Letter, 8.5/11).
  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      const isPortrait = window.innerHeight > window.innerWidth;
      const aspect = pageAspect || 8.5 / 11;
      if (isPortrait) {
        // Mobile-style: leave 32px of breathing room on the sides.
        setPageWidth(Math.max(280, Math.floor(r.width - 32)));
      } else {
        // Desktop: target the available height (stage padding is 56 top
        // and 16 bottom — see App.css). Cap the resulting width so the
        // page never exceeds the stage horizontally.
        const availHeight = Math.max(320, Math.floor(r.height - 16));
        const widthFromHeight = Math.floor(availHeight * aspect);
        const maxWidth = Math.floor(r.width - 32);
        setPageWidth(Math.max(280, Math.min(widthFromHeight, maxWidth)));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [pageAspect]);

  const goPrev = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1));
  }, []);

  const goNext = useCallback(() => {
    setPageNumber((p) => (numPages ? Math.min(numPages, p + 1) : p));
  }, [numPages]);

  // Keyboard arrows + Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  // Touch swipe handlers (mobile app feel)
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    // Only treat as a page turn if the horizontal swipe dominates a vertical
    // one (so vertical scrolling inside a page still works naturally).
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const triggerBrowserDownload = (url, filename) => {
    const a = window.document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/repository-download`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          document_id: doc.id,
          document_title: doc.title,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      triggerBrowserDownload(doc.src, doc.filename || `${doc.id}.pdf`);
      setEmailModalOpen(false);
      setEmail("");
    } catch (err) {
      setError("Something went wrong, please try again.");
      devWarn("[Pandurata] repository download failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pdf-viewer" data-testid="pdf-viewer">
      <Link
        to="/repository"
        className="pdf-viewer__back"
        aria-label="Back to repository"
        data-testid="pdf-viewer-back"
      >
        <ArrowLeft size={18} strokeWidth={1.6} />
      </Link>

      <button
        type="button"
        className="pdf-viewer__download"
        onClick={() => setEmailModalOpen(true)}
        aria-label="Download document"
        data-testid="pdf-viewer-download"
      >
        <Download size={18} strokeWidth={1.6} />
        <span className="pdf-viewer__download-label">Download</span>
      </button>

      <div
        ref={wrapRef}
        className="pdf-viewer__stage"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="pdf-viewer-stage"
      >
        <Document
          file={doc.src}
          onLoadSuccess={({ numPages: n }) => {
            setNumPages(n);
            setLoadError(null);
          }}
          onLoadError={(err) => {
            devWarn("[Pandurata] PDF load error:", err);
            setLoadError(err);
          }}
          loading={
            <div className="pdf-viewer__status">Loading document…</div>
          }
          error={
            <div className="pdf-viewer__status">
              We couldn&apos;t load this document.
            </div>
          }
        >
          {!loadError && (
            <Page
              key={`page-${pageNumber}`}
              pageNumber={pageNumber}
              width={pageWidth}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="pdf-viewer__page"
              onLoadSuccess={(p) => {
                // Capture the natural aspect ratio of the first rendered page
                // so the desktop fit-to-height calculation can use it.
                if (!pageAspect && p?.width && p?.height) {
                  setPageAspect(p.width / p.height);
                }
              }}
            />
          )}
        </Document>
      </div>

      {numPages > 1 && (
        <>
          <button
            type="button"
            className="pdf-viewer__nav pdf-viewer__nav--prev"
            onClick={goPrev}
            disabled={pageNumber <= 1}
            aria-label="Previous page"
            data-testid="pdf-viewer-prev"
          >
            <ChevronLeft size={22} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            className="pdf-viewer__nav pdf-viewer__nav--next"
            onClick={goNext}
            disabled={pageNumber >= numPages}
            aria-label="Next page"
            data-testid="pdf-viewer-next"
          >
            <ChevronRight size={22} strokeWidth={1.6} />
          </button>
        </>
      )}

      <div className="pdf-viewer__caption" data-testid="pdf-viewer-caption">
        <p className="pdf-viewer__title">{doc.title}</p>
        <p className="pdf-viewer__subtitle">
          {doc.subtitle}
          {numPages ? (
            <>
              {" · "}
              <span data-testid="pdf-viewer-pagenum">
                {pageNumber} / {numPages}
              </span>
            </>
          ) : null}
        </p>
      </div>

      {emailModalOpen && (
        <div
          className="email-modal__backdrop"
          role="dialog"
          aria-modal="true"
          data-testid="pdf-email-modal"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setEmailModalOpen(false);
          }}
        >
          <form className="email-modal" onSubmit={submit}>
            <button
              type="button"
              className="email-modal__close"
              onClick={() => setEmailModalOpen(false)}
              aria-label="Close"
              data-testid="pdf-email-modal-close"
            >
              <X size={16} strokeWidth={1.6} />
            </button>

            <p className="email-modal__eyebrow">Before you take this with you</p>
            <h3 className="email-modal__title">Leave us a way to reach you.</h3>
            <p className="email-modal__body">
              Drop your email and the document will begin downloading. Your
              email stays private — never marketed, never shared.
            </p>
            <input
              type="email"
              className="email-modal__input"
              placeholder="you@somewhere.com"
              autoFocus
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              data-testid="pdf-email-modal-input"
            />
            {error && (
              <p
                className="join-list__status join-list__status--err"
                style={{ margin: 0 }}
              >
                {error}
              </p>
            )}
            <div className="email-modal__row">
              <button
                type="button"
                className="email-modal__btn email-modal__btn--ghost"
                onClick={() => setEmailModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="email-modal__btn"
                disabled={!valid || submitting}
                data-testid="pdf-email-modal-submit"
              >
                <Send size={14} />
                <span>{submitting ? "Sending…" : "Download"}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;
